import { isEmpty, isNil, last } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { useGetWorkflowTemplateLazyQuery } from '@shared/scenes/workflows/hooks';
import {
  getComponentIconWithFallback,
  getComponentLabelWithFallback,
  isGroupableComponent,
  isInputComponent,
  isRelatedCardComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import {
  RELATED_ENTITY_MAX_DEPTH,
  RelatedEntityHop,
  RelatedEntityPath,
  RelatedFilterOptions,
} from '../types';
import { getRelatedEntityPathKey } from './relatedEntityPath';
import { toFilterOptions, toGroupOptions } from './toFilterOptions';

// Component types the filter UI can actually filter on. Nested options are restricted to these so
// expanding a related card can't produce a row with no way to pick a value - a related workflow
// may well have a filterable RELATED_CARD_LOOKUP, which has no editor. TIME_TRACKER qualifies only
// through its scalar representations; `toFilterOptions` drops its own value, which has no editor
// (see `canFilterComponentValue`).
const SUPPORTED_NESTED_FILTER_TYPES: ReadonlySet<string> = new Set([
  'DATE',
  'EMAIL',
  'FILE',
  'NUMBER',
  'PERSON',
  'RELATED_CARD',
  'SIGNATURE',
  'TAG',
  'TEXT',
  'TIME_TRACKER',
  'URL',
]);

interface RelatedFilterOptionsResult {
  /** Filterable components at each requested path, keyed by related-card component path. */
  relatedFilters: RelatedFilterOptions;
  /** The groupable subset (tag / person / related card). */
  relatedGroups: RelatedFilterOptions;
  requestRelatedFilterOptions: (paths: string[][]) => void;
}

/** A requested path, resolved as far as the templates loaded so far allow. */
interface ResolvedPath {
  /** The hops resolved so far, outermost first. Shorter than the requested path while pending. */
  hops: RelatedEntityPath;
  /** True once every component in the requested path has been found. */
  isComplete: boolean;
}

/**
 * Resolves the nested filter/group options for cross-workflow filtering.
 *
 * `ControlsProvider` can't call a query hook per related-card component, so this fetches through
 * `useGetWorkflowTemplateLazyQuery` instead. That writes the same Apollo cache entry as
 * `useWorkflowTemplate(id)`, so anything reading the template later is a cache hit.
 *
 * Callers register demand rather than getting everything up front: a related workflow is only
 * fetched once a menu expands that related card, or once applied criteria turns out to reference
 * it (see `getRelatedEntityFilterPaths`).
 *
 * A path of more than one hop resolves a hop at a time: the second hop's component lives on the
 * first hop's workflow, so it can't be looked up until that template has arrived. Each arrival
 * re-runs the resolution below, which is what walks the path forward.
 */
export function useRelatedFilterOptions(components: InputComponent[]): RelatedFilterOptionsResult {
  const getWorkflowTemplate = useGetWorkflowTemplateLazyQuery();
  const [requestedPaths, setRequestedPaths] = useState<string[][]>([]);
  const [templates, setTemplates] = useState<Record<string, API.WorkflowTemplateFragment>>({});
  // Ids already fetched or in flight. A ref rather than state so a re-render mid-fetch can't
  // schedule the same request twice.
  const handledTemplateIds = useRef<Set<string>>(new Set());

  const requestRelatedFilterOptions = useCallback((paths: string[][]) => {
    // A path deeper than the fragment can read back has no options to resolve - see
    // `RELATED_ENTITY_MAX_DEPTH`.
    const wanted = paths.filter(path => !isEmpty(path) && path.length <= RELATED_ENTITY_MAX_DEPTH);
    if (isEmpty(wanted)) {
      return;
    }
    setRequestedPaths(prev => {
      const known = new Set(prev.map(getRelatedEntityPathKey));
      const missing = wanted.filter(path => !known.has(getRelatedEntityPathKey(path)));
      return isEmpty(missing) ? prev : [...prev, ...missing];
    });
  }, []);

  // Requested path key -> the hops resolved for it. Keyed on ids only: components aren't
  // normalized in the Apollo cache, so a parent-template refetch hands back new object identities
  // for otherwise identical components.
  const resolvedPaths = useMemo(() => {
    const resolved = new Map<string, ResolvedPath>();
    requestedPaths.forEach(path => {
      const hops: RelatedEntityHop[] = [];
      let levelComponents = components;
      for (const componentId of path) {
        const component = levelComponents.find(c => c.id === componentId);
        if (
          isNil(component) ||
          !isRelatedCardComponent(component) ||
          isNil(component.workflowTemplateId)
        ) {
          // The path names a component that isn't a related card here - an archived one, or a
          // stale saved filter. Nothing to resolve, now or later.
          return;
        }
        const workflowTemplateId = component.workflowTemplateId;
        hops.push({
          componentId,
          label: getComponentLabelWithFallback(component),
          icon: getComponentIconWithFallback(component),
          workflowTemplateId,
        });
        const template = templates[workflowTemplateId];
        if (isNil(template)) {
          // This hop's workflow hasn't arrived, so nothing below it can be looked up yet. The
          // effect below fetches it and this runs again.
          resolved.set(getRelatedEntityPathKey(path), { hops, isComplete: false });
          return;
        }
        levelComponents = (template.components ?? []).filter(isInputComponent);
      }
      resolved.set(getRelatedEntityPathKey(path), { hops, isComplete: true });
    });
    return resolved;
  }, [requestedPaths, components, templates]);

  // Every template any resolved path travels through, including the innermost one - that is the
  // one whose components become the options.
  const requestedTemplateIds = useMemo(() => {
    const ids = Array.from(resolvedPaths.values()).flatMap(({ hops }) =>
      hops.map(hop => hop.workflowTemplateId),
    );
    return Array.from(new Set(ids));
  }, [resolvedPaths]);

  useEffect(() => {
    // `handledTemplateIds` makes this idempotent, so it doesn't matter that
    // `requestedTemplateIds` can get a fresh identity when the parent template refetches.
    const unfetched = requestedTemplateIds.filter(id => !handledTemplateIds.current.has(id));
    if (isEmpty(unfetched)) {
      return;
    }
    unfetched.forEach(id => handledTemplateIds.current.add(id));
    unfetched.forEach(id => {
      getWorkflowTemplate(id).then(template => {
        if (!isNil(template)) {
          setTemplates(prev => ({ ...prev, [id]: template }));
        }
      });
    });
  }, [requestedTemplateIds, getWorkflowTemplate]);

  const { relatedFilters, relatedGroups } = useMemo(() => {
    const filters: RelatedFilterOptions = new Map();
    const groups: RelatedFilterOptions = new Map();
    resolvedPaths.forEach(({ hops, isComplete }, pathKey) => {
      const innermost = last(hops);
      if (!isComplete || isNil(innermost)) {
        return;
      }
      const template = templates[innermost.workflowTemplateId];
      if (isNil(template)) {
        return;
      }
      const nestedComponents = (template.components ?? []).filter(isInputComponent);
      filters.set(
        pathKey,
        toFilterOptions(
          nestedComponents.filter(component => SUPPORTED_NESTED_FILTER_TYPES.has(component.type)),
          hops,
        ),
      );
      groups.set(pathKey, toGroupOptions(nestedComponents.filter(isGroupableComponent), hops));
    });
    return { relatedFilters: filters, relatedGroups: groups };
  }, [resolvedPaths, templates]);

  return useMemo(
    () => ({
      relatedFilters,
      relatedGroups,
      requestRelatedFilterOptions,
    }),
    [relatedFilters, relatedGroups, requestRelatedFilterOptions],
  );
}
